import type { SchemaFieldDefinition } from "@erp/shared-types";
import type { SocketType } from "../types";
import { extractionSchemaToOutputSockets } from "./extractionSchemaToSockets";

export interface HttpTriggerOutputSocketSpec {
  id: string;
  name: string;
  type: SocketType;
  required: boolean;
  description?: string;
}

export const HTTP_TRIGGER_RESERVED_OUTPUT_SOCKET_IDS = ["payload"] as const;

const SOCKET_ID_REGEX = /^[a-z][a-z0-9_]*$/;

/**
 * Root-level fields from a pinned `SchemaDefinitionSnapshot` (each entry is
 * a top-level request body key; nested shapes live on `children`).
 */
export function filterHttpTriggerRootSchemaFields(
  fields: SchemaFieldDefinition[],
): SchemaFieldDefinition[] {
  return fields.filter((f) => !f.parentFieldId);
}

/**
 * Output sockets for `http_trigger`: one socket per eligible root field in
 * the pinned schema plus the reserved `payload` socket (full body).
 */
export function schemaRootFieldsToHttpTriggerOutputSockets(
  rootFields: SchemaFieldDefinition[],
): HttpTriggerOutputSocketSpec[] {
  const eligible = rootFields.filter((f) => {
    if (f.parentFieldId) return false;
    if (!SOCKET_ID_REGEX.test(f.fieldName)) return false;
    if (
      (HTTP_TRIGGER_RESERVED_OUTPUT_SOCKET_IDS as readonly string[]).includes(
        f.fieldName,
      )
    ) {
      return false;
    }
    return true;
  });

  const perField = extractionSchemaToOutputSockets(eligible, {
    idPrefix: "",
    mode: "per_field",
    respectFieldRequired: true,
  });

  return [
    ...perField.map((s) => ({
      id: s.id,
      name: s.name,
      type: s.type,
      required: s.required,
      description: s.description,
    })),
    {
      id: "payload",
      name: "Payload",
      type: "json" as SocketType,
      required: false,
      description:
        "Entire validated request body as a JSON object. Use this when downstream steps need the whole payload.",
    },
  ];
}
